<template>
  <button @click="show = true" class="btn btn-warning btn-sm">
    <i class="fa fa-search"></i>{{ $t('checkPhoneNumber') }}
    <b-modal v-model="show" :title="$t('checkPhoneNumber')" @close="close" no-close-on-esc no-close-on-backdrop
             no-enforce-focus size="xs">
      <div class="form-group">
        <label><b>{{ $t('downloadTemplate') }}</b></label>
        <div>
          <button @click="downloadTemplate" class="btn btn-sm btn-secondary mr-1 mb-2">
            <i class="fa fa-fw fa-lg fa-file-excel-o"></i>check-phone-number-template.xlsx
          </button>
        </div>
      </div>
      <div class="form-group">
        <label><b>{{ $t('selectFile') }}</b></label>
        <b-form-file class="mb-3"
                     v-model="file"
                     :placeholder="$t('fileInputPlaceholder')"
                     :drop-placeholder="$t('fileInputPlaceholder')"
                     :browse-text="$t('selectFile')"
                     :disabled="reading||totalRows>0"
                     accept="application/x-iwork-keynote-sffnumbers,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ></b-form-file>
      </div>
      <div v-if="reading" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>&nbsp;
        <span>{{ $t('importReadingNotice') }}</span>
      </div>
      <div v-if="importing" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>&nbsp;
        <span>{{ $t('importingNotice') }}</span>
      </div>
      <div class="mb-3" v-if="totalRows">
        <b-progress :max="totalRows">
          <b-progress-bar :value="successCount+errorCount"
                          :label="`${(((successCount + errorCount) / totalRows) * 100).toFixed(0)}%`"></b-progress-bar>
        </b-progress>
        <div class="text-center">{{ successCount + errorCount }}/{{ totalRows }} | {{ $t('error') }}: {{ errorCount }}
          <a v-if="errorCount" @click="downloadErrors" class="btn-link text-primary" href="javascript:">[{{
              $t('detail')
            }}]</a>
        </div>
      </div>

      <template v-slot:modal-footer>
        <button class="btn btn-secondary" @click="cancel" v-if="!reading&&importing">{{ $t('cancel') }}</button>
        <button class="btn btn-primary" @click="reset" v-if="!reading&&!importing&&totalRows>0">Reset</button>
        <button v-if="totalRows===0" class="btn btn-primary" @click="process" :disabled="reading||importing">
          <b-spinner small v-if="reading||importing"></b-spinner>
          {{ $t('check') }}
        </button>
      </template>
    </b-modal>

    <b-modal v-model="showNumberList" :title="$t('numberList')" @close="showNumberList = false" no-close-on-esc
             no-close-on-backdrop content-class="modal-content-2" scrollable
             no-enforce-focus size="xl" header-bg-variant="white" footer-bg-variant="white">

      <number-list :numbers-to-check="numbersToCheck" :hidden-c-sorting-feature="true"/>

      <template v-slot:modal-footer>
        <button class="btn btn-secondary" @click="showNumberList = false">{{ $t('close') }}</button>
      </template>
    </b-modal>
  </button>
</template>

<script>
import fileReaderStream from 'filereader-stream';
import {saveAs} from 'file-saver'
import Excel from 'exceljs';

export default {
  components: {NumberList: () => import('@/views/phone/NumberList.vue')},
  data() {
    return {
      show: false,
      file: null,
      reading: false,
      importing: false,
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      importErrors: [],
      cancelRequest: false,
      numbersToCheck: [],
      showNumberList: false,
    }
  },
  methods: {
    close(evt) {
      if (this.reading || this.importing) {
        evt.preventDefault();
      } else {
        this.reset();
      }
    },
    reset() {
      this.totalRows = 0;
      this.successCount = 0;
      this.errorCount = 0;
      this.importErrors = [];
      this.cancelRequest = false;
      this.reading = false;
      this.importing = false;
      this.file = null;
      this.numbersToCheck = [];
    },
    process(evt) {
      evt.preventDefault();
      if (!this.file) {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('importFileRequired'),
          icon: 'error',
        });
        return;
      }
      if (this.file.name.endsWith('.xls') || this.file.name.endsWith('.xlsx')) {
        this.reading = true;
        let readStream = fileReaderStream(this.file);
        const workbook = new Excel.Workbook();

        workbook.xlsx.read(readStream).then(async () => {
          if (workbook.worksheets.length === 0) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let sheet = workbook.worksheets[0];
          if (sheet.actualRowCount < 3) {
            throw new Error(this.$t('fileHasNoData'));
          }
          if (!sheet.getRow(1).values.find(v => v && v.trim() === 'number')) {
            throw new Error(this.$t('invalidFileTemplate'));
          }

          let headerRow = sheet.getRow(1);
          let i = 3;
          this.totalRows = sheet.actualRowCount - 2;
          this.reading = false;
          this.importing = true;
          this.numbersToCheck = []
          while (i <= sheet.actualRowCount) {
            if (this.cancelRequest) {
              break;
            }
            let item = this.getItem(headerRow, sheet.getRow(i));
            try {
              this.numbersToCheck.push(String(item.number));
              this.successCount++;
            } catch (err) {
              this.importErrors.push({row: i, err: err, number: item.number});
              this.errorCount++;
            }
            i++;
          }
          this.showNumberList = true;
        }).catch(err => {
          this.$swal({
            title: this.$t('error'),
            text: err.message,
            icon: 'error',
          });
        }).finally(async () => {
          this.reading = false;
          this.importing = false;
          this.cancelRequest = false;
        });
      } else {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('invalidFileType'),
          icon: 'error',
        });
      }
    },
    cancel() {
      this.cancelRequest = true;
    },
    downloadErrors() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Import errors');
      sheet.columns = [
        {header: this.$t('rowNumber'), key: 'row', width: 15},
        {header: this.$t('errors'), key: 'error', width: 50},
        {header: this.$t('phoneNumber'), key: 'number', width: 50},
      ];
      for (let err of this.importErrors) {
        sheet.addRow({
          row: err.row,
          error: (err.err.response && err.err.response.status === 422) ? err.err.response.data.map(i => `${i.param}: ${i.msg}`).join(', ') : err.err.message,
          number: err.number ? err.number : ''
        });
      }
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'check-phone-number-import-error.xlsx'
        )
      });
    },
    downloadTemplate: function () {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Callio Contacts');
      this.setupSheet(sheet);

      sheet.addRow({
        number: this.$t("phoneNumber"),
      });

      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'check-phone-number-template.xlsx'
        )
      });
    },
    setupSheet(sheet) {
      sheet.columns = [
        {header: 'number', key: 'number', width: 20},
      ];
    },
    getItem(headerRow, row) {
      let item = {};
      for (let j = 1; j <= headerRow.cellCount; j++) {
        let key = headerRow.getCell(j).value;
        let value;
        if (row.getCell(j).isHyperlink) {
          value = row.getCell(j).value.text;
        } else {
          value = row.getCell(j).value;
        }
        if (row.getCell(j).result) {
          value = row.getCell(j).result
        }
        if (!value) {
          item[key] = value;
          continue;
        } else {
          item[key] = value;
        }
      }
      item = {...item, ...this.formModel}
      return item;
    }
  },
  i18n: {
    messages: {
      vi: {
        checkPhoneNumber: "Kiểm tra số điện thoại",
        check: "Kiểm tra",
        downloadTemplate: "Tải file mẫu",
        selectFile: "Chọn file",
        fileInputPlaceholder: "Chọn file từ máy tính hoặc kéo thả file vào đây",
        importReadingNotice: "Đang đọc file. Quá trình này có thể sẽ tốn nhiều thời gian nếu file tải lên có dung lượng lớn. Xin đừng tắt cửa sổ trình duyệt cho đến khi quá trình tải lên hoàn tất!",
        invalidFileTemplate: "File tải lên chưa đúng mẫu!",
        importingNotice: "Đang tải dữ liệu lên... Xin hãy kiên nhẫn, việc tải lên sẽ mất một chút thời gian nếu file của bạn quá lớn",
        fileHasNoData: "File không có dữ liệu. Xin hãy kiểm tra lại!",
        importFileRequired: "Bạn cần chọn file tải lên!",
        numberList: 'Danh sách số điện thoại đã tồn tại',
        close: 'Đóng',
      },
      en: {
        checkPhoneNumber: "Check phone number",
        check: "Check",
        downloadTemplate: "Download sample file",
        selectFile: "Select file",
        fileInputPlaceholder: "Choose a file from your computer or drag and drop it here",
        importReadingNotice: "Reading file. This may take a while if the file is large. Please do not close your browser window until the upload is complete!",
        invalidFileTemplate: "The uploaded file is not in the correct format!",
        importingNotice: "Uploading data... Please be patient, it will take a while if your file is large.",
        fileHasNoData: "File has no data. Please check again!",
        importFileRequired: "File is required!",
        numberList: 'List of existing phone numbers',
        close: 'Close',
      }
    }
  }
}
</script>

<style>
.modal-content-2 {
  background-color: #e5e5e5;
}
</style>